import { observer } from 'mobx-react-lite';
import {
    attachButtonLabel,
    attachFromMenuHeaderText,
    attachFromComputerButtonLabel,
    attachFromCloudButtonLabel,
} from './AttachToolbarButton.locstring.json';
import { attachmentsNotSupportedTooltip } from './FullComposeEditorAreaBottom.locstring.json';
import loc from 'owa-localize';
import logAttachMenuData from '../utils/logAttachMenuData';
import { IconButton } from '@fluentui/react/lib/Button';
import { Image } from '@fluentui/react/lib/Image';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { logUsage } from 'owa-analytics';
import { attachmentMultiMenuEntry } from 'owa-attachment-multi-menu-actions';
import { getOwaResourceUrl } from 'owa-resource-url';

import * as React from 'react';
import {
    ContextualMenu,
    ContextualMenuItemType,
    DirectionalHint,
    IContextualMenuItem,
} from '@fluentui/react/lib/ContextualMenu';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import { getComposeEvent } from 'owa-calendar-compose-store-manager';
import { isConnectedAccount } from 'owa-accounts-store';

const ATTACH_SVG = require('./icons/attach.svg');
const DROPDOWN_SVG = require('./icons/dropdown.svg');

let styles = require('./FullComposeEditorAreaBottom.scss');
let classNames = require('classnames/bind').bind(styles);

export const enum AttachButton {
    COMPUTER_ATTACHMENT_KEY = 'addMyComputerAttachment',
    ONLINE_LOCATION_KEY = 'addOnlineLocationAttachment',
    UPLOAD_AND_SHARE_FROM_CLOUD_KEY = 'addUploadAndShareFromCloud',
}

export interface AttachToolbarButtonProps {
    openCloudFilePicker: () => void;
    openLocalComputerFilePickerAndAttach: () => void;
    openLocalComputerFilePickerAndShare: () => void;
}

export interface AttachToolbarButtonState {
    isAttachmentMenuShown: boolean;
}

export default observer(function AttachToolbarButton(props: AttachToolbarButtonProps) {
    const attachButton = React.useRef<HTMLElement>();
    const [isAttachmentMenuShown, setIsAttachmentMenuShown] = React.useState<boolean>(false);
    const onToggleAttachmentMenu = () => {
        setIsAttachmentMenuShown(!isAttachmentMenuShown);
    };
    const disableAttachments = shouldDisableAttachments();
    const onMenuOpened = React.useCallback(() => {
        // Log that the Attach button itself was clicked.
        logUsage('AttachButtonClicked', [false /* isMail */]);
        // Log info about the buttons shown in the menu.
        logAttachMenuData(getDefaultButtonsList());
    }, []);

    const onMenuDismissed = React.useCallback(() => {
        // Log that the menu has been dismissed.
        logUsage('AttachButtonMenuClosed', [false /* isMail */]);
    }, []);

    const getAttachmentCommandItems = React.useCallback((): IContextualMenuItem[] => {
        const buttons: AttachButton[] = getDefaultButtonsList();
        // Start with header then add buttons dependiong on what getDefaultButtonsList returns.
        const menuItems: IContextualMenuItem[] = [
            {
                key: 'attachFromHeader',
                name: loc(attachFromMenuHeaderText),
                itemType: ContextualMenuItemType.Header,
            },
        ];
        if (buttons.indexOf(AttachButton.COMPUTER_ATTACHMENT_KEY) >= 0) {
            menuItems.push({
                key: AttachButton.COMPUTER_ATTACHMENT_KEY,
                name: loc(attachFromComputerButtonLabel),
                iconProps: attachmentMultiMenuEntry.computer.iconProps,
                onClick: props.openLocalComputerFilePickerAndAttach,
            });
        }
        if (buttons.indexOf(AttachButton.ONLINE_LOCATION_KEY) >= 0) {
            menuItems.push({
                key: AttachButton.ONLINE_LOCATION_KEY,
                name: loc(attachFromCloudButtonLabel),
                iconProps: attachmentMultiMenuEntry.onlineLocation.iconProps,
                onClick: props.openCloudFilePicker,
            });
        }
        if (buttons.indexOf(AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY) >= 0) {
            menuItems.push({
                key: AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY,
                name: attachmentMultiMenuEntry.uploadAndShareFromCloud.text(),
                ariaLabel: attachmentMultiMenuEntry.uploadAndShareFromCloud.ariaLabel(),
                title: attachmentMultiMenuEntry.uploadAndShareFromCloud.ariaLabel(),
                iconProps: attachmentMultiMenuEntry.uploadAndShareFromCloud.iconProps,
                onClick: props.openLocalComputerFilePickerAndShare,
            });
        }
        return menuItems;
    }, [
        props.openLocalComputerFilePickerAndAttach,
        props.openCloudFilePicker,
        props.openLocalComputerFilePickerAndShare,
    ]);
    return (
        <TooltipHost
            content={
                disableAttachments ? loc(attachmentsNotSupportedTooltip) : loc(attachButtonLabel)
            }>
            <span ref={ref => (attachButton.current = ref)} className={styles.attachButton}>
                <IconButton
                    disabled={disableAttachments}
                    onClick={onToggleAttachmentMenu}
                    ariaLabel={
                        disableAttachments
                            ? loc(attachmentsNotSupportedTooltip)
                            : loc(attachButtonLabel)
                    }
                    className={classNames(
                        styles.toolbarButton,
                        {
                            checked: isAttachmentMenuShown,
                            connectedAccount: disableAttachments,
                        },
                        styles.attachToolbarButton
                    )}>
                    <Image
                        className={classNames(styles.image, styles.attachImage)}
                        shouldFadeIn={false}
                        src={getOwaResourceUrl(ATTACH_SVG)}
                        alt=""
                    />
                    <Image
                        className={classNames(styles.image, styles.chevronImage)}
                        shouldFadeIn={false}
                        src={getOwaResourceUrl(DROPDOWN_SVG)}
                        alt=""
                    />
                </IconButton>
                {isAttachmentMenuShown && (
                    <ContextualMenu
                        items={getAttachmentCommandItems()}
                        target={attachButton.current}
                        gapSpace={10}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        onDismiss={onToggleAttachmentMenu}
                        onMenuOpened={onMenuOpened}
                        onMenuDismissed={onMenuDismissed}
                    />
                )}
            </span>
        </TooltipHost>
    );
});

function shouldDisableAttachments() {
    const event = getComposeEvent(null /* formId */);
    return (
        /* Block attachments when creating event in connected account */
        isConnectedAccount(event.ParentFolderId.mailboxInfo.userIdentity) ||
        (!classicAttachmentsEnabled() && !referenceAttachmentsEnabled())
    );
}

function classicAttachmentsEnabled() {
    return getAttachmentPolicy().ClassicAttachmentsEnabled;
}

function referenceAttachmentsEnabled() {
    return getAttachmentPolicy().ReferenceAttachmentsEnabled;
}

/**
 * Returns a list of button names (email/cloud/local etc) shown in the Attach button.
 * Used for logging. Needs to be consistent with getAttachmentCommandItems method.
 */
/**
 * Returns a list of button names (email/cloud/local etc) shown in the Attach button.
 * Used for logging. Needs to be consistent with getAttachmentCommandItems method.
 */
function getDefaultButtonsList(): AttachButton[] {
    const defaultButtons: AttachButton[] = [];
    if (classicAttachmentsEnabled()) {
        defaultButtons.push(AttachButton.COMPUTER_ATTACHMENT_KEY);
    }
    if (classicAttachmentsEnabled() || referenceAttachmentsEnabled()) {
        defaultButtons.push(AttachButton.ONLINE_LOCATION_KEY);
    }
    if (attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled()) {
        defaultButtons.push(AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY);
    }
    return defaultButtons;
}
